import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Truck, Shield, Award, Users, Clock, CheckCircle2, Cpu, ArrowRight } from 'lucide-react';
import { siteImages } from '../data/mock';

const WhyChooseUsPage = () => {
  useEffect(() => {
    document.title = 'Why Choose Us | EC Tool Works';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="">
        {/* Banner Section */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.aboutBanner}
              alt="Why Choose Us"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-orange-700 text-xs font-semibold mb-4">
                <Cpu className="w-3.5 h-3.5" />
                OUR DIFFERENCE
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
                Why Choose EC Tool Works?
              </h1>
              <p className="text-gray-600 max-w-lg">
                We're committed to providing the best hardware tools and customer experience
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main Benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Truck, title: 'Fast USA Shipping', desc: 'Free USA shipping on every order. Quick delivery to your door.' },
              { icon: Shield, title: 'Secure Checkout', desc: 'Your payment information is always protected with SSL encryption.' },
              { icon: Award, title: 'Quality Hardware', desc: 'Every product is tested for strength and precision before we add it to our catalog.' },
              { icon: Users, title: 'Customer Support', desc: 'Our dedicated team is here to help you find the right hardware for the job.' },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-athletic-500/50 transition-all group">
                <div className="w-14 h-14 bg-gradient-to-br from-athletic-500 to-athletic-600 rounded-xl flex items-center justify-center mb-5 group-hover:shadow-glow-athletic transition-shadow">
                  <item.icon className="w-7 h-7 text-gray-900" />
                </div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Additional Features */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-12 mb-16">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl font-extrabold text-gray-900 mb-4">
                The EC Tool Works Difference
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Here's what sets us apart from other hardware tool retailers
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Carefully curated product selection',
                'Premium quality at fair prices',
                '30-day hassle-free returns',
                'Fast processing and shipping',
                'Secure payment processing',
                'Detailed product descriptions',
                'Responsive customer support',
                'Easy-to-use website',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-gray-100 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-slate-700 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-br from-athletic-500/10 to-athletic-600/10 border border-slate-200 rounded-2xl p-12">
            <h2 className="font-display text-3xl font-extrabold text-gray-900 mb-4">
              Ready to Gear Up Your Workshop?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Browse our selection of reliable hardware tools and experience the EC Tool Works difference.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-athletic-500 to-athletic-600 text-gray-900 font-bold uppercase tracking-wider rounded-xl hover:from-athletic-400 hover:to-athletic-500 transition-all shadow-lg"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WhyChooseUsPage;
